import { Dialog } from '@/components/_common';
import useOnClickOutside from '@/components/_hooks/useOnClickOutside';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectOutputVersions, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { addInventorySelections, removeOutputVersion, removeOutputVersionFile, updateOutputVersion } from '@/redux/slices/orderPlacementSlice';
import { FormJob, InventoryFileEntity } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import SchemaDisplay from '../../../_common/SchemaForm/SchemaDisplay';
import useProfile from '../../../_hooks/useProfile';
import OPFileSelectionTitle from '../FileSelections/OPFileSelectionTitle';
import InventoryFile from '@/components/_common/InventoryFile/InventoryFile';
import PrimaryStarButton from '../PrimaryStarButton';
import OPOutputVersionsEdit from './OPOutputVersionsEdit';
import OutputVersionHeader from './OutputVersionHeader';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { Inventory } from '@mui/icons-material';

// *******
// Component on Advanced/Component ordering flow on right side of screen
// of an individual output version that can be edited
// It is an accordion that contains selected files and title inputs
// While editing, clicking outside the accordion should prompt the user
// to save or discard changes only if changes have been made
// *******

interface FormValues {
  jobs: FormJob[];
}

interface OPOutputVersionProps {
  group: any;
  files: InventoryFileEntity[];
  expanded: boolean;
  editMode: boolean;
  index: number;
  isReview: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  toggleExpand: () => void;
}

const OPOutputVersion = ({
  group,
  files,
  expanded = false,
  editMode = false,
  index,
  isReview,
  onSave,
  onEdit,
  onCancel,
  toggleExpand
}: OPOutputVersionProps) => {
  const dispatch = useAppDispatch();
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { titleSchema } = useProfile(values);
  const selectedJobs = useAppSelector(selectOutputVersions);
  const versionKeys = useAppSelector(selectVersionKeys);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasChanges = useRef(false);
  const data = selectedJobs.find((job: any) => job.id === group.id);
  const titleSchemaFormRef = useRef<any>(null);
  const [formValues, setFormValues] = useState(data ? data.title_inputs : {});

  const setHasChanges = () => {
    hasChanges.current = true;
  };

  useOnClickOutside(wrapperRef, () => {
    if (editMode) {
      hasChanges.current ? setConfirmDialogOpen(true) : onCancel();
    }
  });

  const removeFile = (fileId: number | string) => {
    dispatch(removeOutputVersionFile({ groupId: group.id, fileId }));
    setHasChanges();
  };

  const handleChange = (title_inputs: any) => {
    dispatch(updateOutputVersion({ ...data, title_inputs }));
    setHasChanges();
  };

  const updatePrimaryVersion = (id: number | string) => {
    dispatch(updateOutputVersion({ ...data, primary_version: id }));
    setHasChanges();
  };

  const handleCancel = () => {
    onCancel();
    hasChanges.current = false;
    setConfirmDialogOpen(false);
  };

  const handleSave = () => {
    // validate title schema form
    const isValid = titleSchemaFormRef.current?.validateForm();
    if (isValid) {
      onSave();
      hasChanges.current = false;
      setConfirmDialogOpen(false);
    }
  };

  const updateAndSave = () => {
    handleChange(formValues);
    handleSave();
  };

  const onDuplicate = () => {
    dispatch(addInventorySelections(files));
    setFieldValue('jobs[0].title_inputs', group.title_inputs);
  };

  const onRemove = () => {
    dispatch(removeOutputVersion(group.id));
  };

  return (
    <OutputVersionWrapper editMode={editMode} ref={wrapperRef}>
      {editMode && (
        <Dialog
          title="Unsaved Changes"
          open={confirmDialogOpen}
          onCancel={handleCancel}
          onSubmit={updateAndSave}
          cancelText="Discard"
          submitText="Save Changes"
        >
          You have unsaved changes. Do you want to discard or save them?
        </Dialog>
      )}
      <Accordion disableGutters expanded={expanded} onChange={toggleExpand}>
        <AccordionSummary expandIcon={<PolarisIcon icon="right" height={20} />} sx={{ pl: 2 }}>
          <OutputVersionHeader
            files={files}
            group={group}
            isReview={isReview}
            jobNumber={index + 1}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onRemove={onRemove}
          />
        </AccordionSummary>
        <AccordionDetails>
          <>
            <Divider sx={{ mb: 2 }} />
            {Object.keys(group.selections).map((id, index) => (
              <Box key={`${id}_${index}`} sx={{ mb: 4 }}>
                {/* if multiple versions are selected, display primary star button */}
                {Object.keys(group.selections).length > 1 && (
                  <Grid container flexWrap="nowrap" alignItems="center">
                    <PrimaryStarButton selected={group.primary_version == id} disabled={!editMode} onClick={() => updatePrimaryVersion(id)} />
                    <OPFileSelectionTitle id={id} version={versionKeys[id]} />
                  </Grid>
                )}
                {group.selections[id].map((file: InventoryFileEntity) => (
                  <InventoryFile
                    key={file.id}
                    file={file}
                    disableDownloads
                    disablePreviews
                    disableSelected
                    isReview={isReview}
                    onRemove={editMode ? () => removeFile(file.id) : undefined}
                    noOverflowMenu
                    showTitleLabel
                  />
                ))}
              </Box>
            ))}

            {group.title_inputs &&
              titleSchema?.properties &&
              (editMode ? (
                <OPOutputVersionsEdit
                  formValues={formValues}
                  setFormValues={setFormValues}
                  titleId={versionKeys[group.primary_version]?.program_id}
                  onCancel={onCancel}
                  updateAndSave={updateAndSave}
                  primaryVersion={group.primary_version}
                  setHasChanges={setHasChanges}
                  titleSchemaFormRef={titleSchemaFormRef}
                />
              ) : (
                <Box sx={{ mb: 1, mt: -1 }}>
                  <SchemaDisplay schema={titleSchema} inputs={group.title_inputs} />
                </Box>
              ))}
          </>
        </AccordionDetails>
      </Accordion>
    </OutputVersionWrapper>
  );
};

export default OPOutputVersion;

const OutputVersionWrapper = styled.div<{ editMode: boolean }>`
  ${({ editMode, theme }) =>
    editMode &&
    `
    .MuiAccordion-root.MuiAccordion-root {
      border: 1px solid ${theme.palette.primary.main};
    }
  `}
`;
